import { useStore } from '@xyflow/react';
import type { EntityNode as EntityNodeType } from '../graph/types';
import { EntityPill } from './EntityPill';

interface UnlinkedEntitiesPanelProps {
  entities: EntityNodeType[];
  onEntityClick?: (entityId: number) => void;
}

// Floor scale at 0.6 so panel pills stay legible on aggressive zoom-out.
const MIN_SCALE = 0.6;

// Toolbar sits at bottom:24 + 40px tall; reserve 80px so the panel clears it.
const TOOLBAR_RESERVE = 80;

// Must render inside <ReactFlowProvider> — reads viewport zoom via useStore.
const UnlinkedEntitiesPanel = ({
  entities,
  onEntityClick,
}: UnlinkedEntitiesPanelProps) => {
  const zoom = useStore((s) => s.transform[2]);
  // Cap at 1 so panel doesn't outgrow canvas above native zoom.
  const scale = Math.max(MIN_SCALE, Math.min(1, zoom));

  if (entities.length === 0) return null;

  return (
    <div
      // pointer-events-none on outer lets canvas pan in the dead zone below pills.
      // Inline top/right since spacing-token classes aren't reliably picked up
      // by the consuming app's content scanner from this package.
      className="pointer-events-none absolute z-10 flex flex-col"
      style={{ top: 8, right: 8, bottom: TOOLBAR_RESERVE }}
    >
      <div
        className="pointer-events-auto flex max-h-full flex-col overflow-y-auto"
        style={{ padding: 8, gap: 8 }}
      >
        {entities.map((e) => (
          <EntityPill
            key={e.id}
            legalName={e.data.legalName}
            incorporationCountry={e.data.incorporationCountry}
            businessEntityType={e.data.businessEntityType}
            dashed
            disabled={e.data.isNonControlledEntity}
            scale={scale}
            onClick={
              onEntityClick ? () => onEntityClick(Number(e.id)) : undefined
            }
          />
        ))}
      </div>
    </div>
  );
};

export { UnlinkedEntitiesPanel };
